import { Component, Inject, OnInit, ViewChild, ViewContainerRef, ComponentRef, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ModalWrapperDataProps } from './types/props.types';

/**
 * ModalWrapperComponent
 *
 * This component serves as a wrapper for dynamically loaded components within a modal dialog.
 * It provides a flexible and reusable structure for displaying various components in a modal context.
 *
 * Features:
 * - Dynamically loads and renders a specified component within the modal.
 * - Handles the lifecycle of the dynamically created component.
 * - Supports passing props to the rendered component.
 * - Provides a mechanism to close the modal.
 *
 * Usage:
 * This component is typically used in conjunction with the ModalWrapperService, which handles
 * the opening and configuration of the modal dialog.
 *
 * @example
 * // In a service or component where you want to open a modal
 * this.modalWrapperService.open(YourComponent, {
 *   dialogTitle: 'Your Modal Title',
 *   componentProps: { someProperty: 'value' },
 *   // ... other MatDialogConfig options
 * });
 */
@Component({
  selector: 'pro-modal-wrapper',
  templateUrl: './modal-wrapper.component.html',
  styleUrls: ['./modal-wrapper.component.scss'],
})
export class ModalWrapperComponent implements OnInit, OnDestroy {
  /**
   * ViewChild reference to the container where the dynamic component will be rendered.
   */
  @ViewChild('componentContainer', { read: ViewContainerRef, static: true })
  componentContainer!: ViewContainerRef;

  /**
   * Reference to the dynamically created component.
   */
  private componentRef!: ComponentRef<any>;

  /**
   * @param data Injected data for the modal, including:
   *   - title: The title of the modal dialog.
   *   - renderComponent: The component type to be dynamically rendered.
   *   - componentProps: Optional properties to be passed to the rendered component.
   *   - close: Function to close the modal.
   *   - events: Optional events to be listened on the rendered component.
   */
  constructor(@Inject(MAT_DIALOG_DATA) public data: ModalWrapperDataProps) {}

  /**
   * Initializes the component by loading the dynamic component.
   */
  ngOnInit() {
    this.loadComponent();
  }

  /**
   * Loads and renders the dynamic component specified in the data.
   * If componentProps are provided, they are assigned to the component instance.
   */
  private loadComponent() {
    this.componentContainer.clear();
    this.componentRef = this.componentContainer.createComponent(this.data.renderComponent);

    if (this.data.componentProps) {
      Object.assign(this.componentRef.instance, this.data.componentProps);
    }

    // Subscribe to component events
    if (this.data.events) {
      Object.keys(this.data.events).forEach((eventName) => {
        if (this.componentRef.instance[eventName]) {
          this.componentRef.instance[eventName].subscribe((data: any) => {
            this.data.events[eventName](data);
          });
        }
      });
    }
  }

  /**
   * Closes the modal dialog.
   */
  public close() {
    this.data.close();
  }

  /**
   * Cleans up resources by destroying the dynamically created component.
   */
  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
      // Unsubscribe from all events
      if (this.data.events) {
        Object.keys(this.data.events).forEach((eventName) => {
          this.componentRef.instance[eventName]?.unsubscribe();
        });
      }
    }
  }
}
